import { useTranslation } from "react-i18next";
import TrackClaim from "components/ServicesAndClaims/TrackClaim";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { isAgentPortal, isFlagEnabled } from "js/utilities";
import { useSelector } from "react-redux";
import { useRowPagination } from "hooks/useRowPagination";
import { useNavigate } from "react-router-dom";
import { ClaimsRequest } from "./Claims/ClaimsRequest";
import { useEffect, useState } from "react";
import { getName, getPoliciesOptions, getPolicyType } from "js/formatting";
import { getPrimaryOwnerFromParties } from "js/utilities";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import CustomerRowPagination from "components/General/CustomerRowPagination";

export default function Claims() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { claims, policies } = useSelector((state) => state.data);
  const [claimsList, setClaimsList] = useState(claims);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState({
    displayAs: "All Contracts",
    selectedPolicyDisplayAs: "All Contracts",
    value: "all",
  });

  const getPrimaryOwnerInfo = (policy) => {
    const primaryOwner = getPrimaryOwnerFromParties(policy?.parties);
    return getName(primaryOwner?.person);
  };

  const filterClaims = (val) => {
    setSelectedPolicy(val);
    setClaimsList(
      val === "all"
        ? claims
        : claims?.filter((claim) => claim.oldPolicy === val)
    );
  };

  useEffect(() => {
    const policySelectionArray = getPoliciesOptions(policies);
    let policiesOptions = [];
    policySelectionArray.map((policy) => {
      policiesOptions.push({
        ...policy,
        selectedPolicyDisplayAs: `${policy.prodType}: ${policy.value}`,
      });
    });

    setOptions([selectedOption, ...policiesOptions]);

    if (policiesOptions.length === 1) {
      setSelectedOption(policiesOptions[0]);
      setSelectedPolicy(policies[0]?.oldPolicy);
    }
  }, [policies]);

  useEffect(() => {
    filterClaims(selectedOption.value);
  }, [selectedOption]);

  const {
    rowsForDisplay: claimRows,
    rowPaginationProps: claimsPaginationProps,
  } = useRowPagination(claimsList, [3, 10, 25, "all"]);

  useBreadcrumb(
    isFlagEnabled("ENABLE_SERVICE_TRACKING")
      ? [
          {
            name: t("CustomerClaims-home-text"),
            url: !isAgentPortal() ? "/policies" : "/dashboard",
          },
          { name: t("CustomerClaims-claims-text"), url: "#" },
        ]
      : [
          {
            name: t("CustomerClaims-home-text"),
            url: !isAgentPortal() ? "/policies" : "/dashboard",
          },
          // { name: "Claims", url: "/services-claims" },
          { name: t("CustomerClaims-claims-text"), url: "#" },
        ]
  );

  return (
    <>
      {/* SPRINT-3-CUSTOMER-claims-and-forms.pdf (pgs 20-22) */}
      <div className="container mb-8">
        <div className="flex justify-between">
          <div>
            <h1>{t("CustomerClaims-claimsTitle")}</h1>
            <p>{t("CustomerClaims-claimsSubTitle-2")}</p>
          </div>
          <button
            className="ui-btn-primary mt-12 flex"
            onClick={() => navigate("/services-claims/claims/file-a-claim")}
          >
            <svg
              className="ui-icon w-4  mt-1 mr-2"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href={"#ui-icon-edit"}></use>
            </svg>
            <span>{t("CustomerClaims-button")}</span>
          </button>
        </div>
        <hr />
      </div>

      <div className="container ui-component-spacing mb-8">
        <h3>
          <b>{t("CustomerClaims-heder-text")}</b>
        </h3>
        <p className="italic w-[57%]">
          {t("CustomerClaims-heder-discription")}
        </p>
        <div className="relative mt-1 ">
          {isAgentPortal() ? (
            <>
              <div className="flex items-center mb-1 pt-4">
                <label id="formsDocumentProduct">
                  {t("CustomerClaims-policy-label")}
                </label>
              </div>
              <div className="flex justify-between">
                <div className="relative w-[40%]">
                  <select
                    id="productId"
                    className="pr-0"
                    onChange={(e) => filterClaims(e.target.value)}
                    value={selectedPolicy}
                  >
                    <option value="all">
                      {t("CustomerClaims-policy-option")}
                    </option>
                    {policies?.length > 0 && (
                      <>
                        {policies.map((p) => (
                          <option
                            key={`forms - ${p.oldPolicy}`}
                            value={p.oldPolicy}
                          >
                            {p?.oldPolicy} - {getPrimaryOwnerInfo(p)}-
                            {getPolicyType(p)}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <p className="flex justify-end mt-5">
                  {t("CustomerClaims-status-text")}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between pt-4 mb-1">
                <div className="relative w-[40%]">
                  <RELEASING_AutoCompleteComboBox
                    labelName={t(
                      "CustomerAlertsCorrespondence-contract-number"
                    )}
                    inputPlaceholderText={t(
                      "AutoComplate-select-your-contract"
                    )}
                    options={options}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                    readOnly={options.length <= 2}
                    showBadge={true}
                  />
                </div>
                <p className="flex justify-end mt-5">
                  {t("CustomerClaims-status-text")}
                </p>
              </div>
            </>
          )}
        </div>
        <ClaimsRequest data={claimRows} type={"claims"} />
        {claimRows.length > 0 ? (
          <CustomerRowPagination {...claimsPaginationProps} />
        ) : (
          <h3 className="text-center mt-8">
            {t("CustomerClaims-noClaimsDisclaimer")}
          </h3>
        )}
      </div>

      <div className="relative container ui-component-spacing lg:pb-[4.5rem]">
        <div className="lg:absolute lg:left-0 lg:right-0 lg:-mt-[5.25rem]">
          <TrackClaim />
        </div>
      </div>
    </>
  );
}
